import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

interface OrganizationNode {
  id: string;
  name: string;
  type: 'company' | 'department' | 'user';
  children?: OrganizationNode[];
  role?: string;
  companyid?: string;
  assistantNo?: string;
  expanded?: boolean;
  userCount?: number;
  users?: any[];
}

@Component({
  selector: 'app-organization-tree-node',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './organization-tree-node.component.html',
  styleUrls: ['./organization-tree-node.component.scss']
})
export class OrganizationTreeNodeComponent {
  @Input() node!: OrganizationNode;
  @Input() selectedDepartment: OrganizationNode | null = null;
  @Input() isRootNode: boolean = false;
  @Input() level: number = 0;

  @Output() nodeSelected = new EventEmitter<OrganizationNode>();
  @Output() nodeToggled = new EventEmitter<OrganizationNode>();

  selectNode(node: OrganizationNode) {
    this.nodeSelected.emit(node);
  }

  toggleNode(node: OrganizationNode, event: Event) {
    event.stopPropagation();
    this.nodeToggled.emit(node);
  }

  isNodeSelected(node: OrganizationNode): boolean {
    return this.selectedDepartment?.id === node.id;
  }

  isExpandable(node: OrganizationNode): boolean {
    return !!(node.children && node.children.some(child => child.type === 'department'));
  }

  getNodeIconClass(node: OrganizationNode): string {
    if (this.isRootNode) {
      return 'fas fa-building';
    }

    switch (this.level) {
      case 1: return 'fas fa-folder-open';
      case 2: return 'fas fa-folder';
      case 3: return 'fas fa-folder-minus';
      default: return 'fas fa-folder-plus';
    }
  }

  getNodeClass(): string {
    if (this.isRootNode) {
      return 'company-node';
    }

    switch (this.level) {
      case 1: return 'department-node';
      case 2: return 'sub-department-node';
      case 3: return 'sub-sub-department-node';
      default: return 'deep-department-node';
    }
  }

  getDisplayUserCount(node: OrganizationNode): number {
    if (node.userCount !== undefined) {
      return node.userCount;
    }

    if (node.users) {
      return node.users.length;
    }

    // 使用 Set 來去重，遞歸計算所有子節點的用戶數量
    const userIds = new Set<string>();
    this.collectUniqueUserIds(node, userIds);
    return userIds.size;
  }

  // 收集唯一的用戶 ID，避免重複計算
  private collectUniqueUserIds(node: OrganizationNode, userIds: Set<string>): void {
    if (!node || !node.children) return;

    node.children.forEach(child => {
      if (child.type === 'user' && child.id) {
        userIds.add(child.id);
      } else if (child.type === 'department') {
        this.collectUniqueUserIds(child, userIds);
      }
    });
  }

  getDepartmentChildren(node: OrganizationNode): OrganizationNode[] {
    if (!node.children) return [];
    return node.children.filter(child => child.type === 'department');
  }
}
